import React from 'react'
import { Box, Button, MenuItem, Alert } from '@mui/material'
import { SxProps, Theme } from '@mui/material'
import FormField from 'components/FormField'
import { AvailabilityCheckForm, CountNursesGroupByTierResults } from 'types/interfaces'
import { PaymentOption, Role } from 'types/types'
import update from 'immutability-helper'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import AvailabilityCheckFormError from 'errors/AvailabilityCheckFormError'
import MultiSelect from 'components/MultiSelect'

const style: SxProps<Theme> = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: ['80%', '80%', '50%'],
    maxHeight: '90%',
    bgcolor: 'background.default',
    border: '2px solid #000',
    boxShadow: 24,
    padding: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 1
}

interface AvailabilityCheckModalProps {
  onSuccess: () => void
  onCancel: () => void
}

const AvailabilityCheckModal: React.FC<AvailabilityCheckModalProps> = ({
  onSuccess,
  onCancel
}) => {
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()
    const [form, setForm] = React.useState<AvailabilityCheckForm>({
        role: 'dental nurse',
        sedation_required: false,
        radiography_required: false,
        implant_required: false,
        orthodontics_required: false,
    })
    const [errors, setErrors] = React.useState<AvailabilityCheckFormError>()
    const [nurseCounts, setNurseCounts] = React.useState<CountNursesGroupByTierResults>()
    const [loading, setLoading] = React.useState<boolean>(false)

    const updateForm = React.useCallback((name: string, value: any) => {
        setForm(update(form, { [name]: { $set: value } }))
        if (errors) setErrors(update(errors, { [name]: { $set: [] } }))
    }, [form, errors])

    const prepopulateCoordinates = React.useCallback(async () => {
        try {
            if (!form.postcode) return
            const coordinates = await api.getCoordinatesFromPostcode(form.postcode)
            setForm(update(form, { 
                lat: { $set: coordinates.latitude }, 
                lng: { $set: coordinates.longitude },
            }))
        } catch (e) {
            console.error('error getting coordinates from postcode', e)
        }
    }, [api, form])

    const getNurseCounts = React.useCallback(async () => {
        try {
            if (!form.lat || !form.lng) return
            const results = await api.countNursesGroupByTier({
                lat: form.lat,
                lng: form.lng,
                role: form.role ?? 'dental nurse',
                sedation_required: form.sedation_required ?? false,
                radiography_required: form.radiography_required ?? false,
            })
            setNurseCounts(results)
        } catch (e) {
            console.error('error getting nurse counts', e)
        }
    }, [api, form])

    const save = React.useCallback(async () => {
        try {
            setLoading(true)
            await api.createAvailabilityCheck(form)
            onSuccess()
        } catch (e) {
            if (e instanceof AvailabilityCheckFormError) {
                setErrors(e)
            } else {
                showAlert('error', 'Error creating availability check')
                console.error('unhandled exception', e)
            }
        } finally {
            setLoading(false)
        }
    }, [api, form, onSuccess, showAlert])

    React.useEffect(() => {
        getNurseCounts()
    }, [getNurseCounts])

    const paymentOptions: { value: PaymentOption; label: string }[] = [
        { value: 'pay_eod_with_card', label: 'Pay EOD with Card' },
        { value: 'invoice_weekly_pay_manually', label: 'Invoice Weekly Pay Manually' },
        { value: 'invoice_biweekly_pay_manually', label: 'Invoice Biweekly Pay Manually' },
        { value: 'invoice_monthly_pay_manually', label: 'Invoice Monthly Pay Manually' },
    ]

    const roleOptions: { value: Role; label: string }[] = [
        { value: 'dental nurse', label: 'Dental Nurse' },
        { value: 'hygienist', label: 'Hygienist' },
    ]

    return (
        <Box sx={style}>
            <FormField
                type='datetime-local'
                name='start_at'
                label="Start At (UK Timezone)"
                value={form.start_at ?? ''}
                onChange={(e) => updateForm('start_at', e.target.value)}
                errors={errors?.start_at}
            />
            <FormField
                type='datetime-local'
                name='end_at'
                label="End At (UK Timezone)"
                value={form.end_at ?? ''}
                onChange={(e) => updateForm('end_at', e.target.value)}
                errors={errors?.end_at}
            />
            <FormField 
                name='lunch_break' 
                label='Lunch Break (minutes)' 
                type='number' 
                value={form.lunch_break ?? ''}
                onChange={(e) => updateForm('lunch_break', e.target.value ? Number(e.target.value) : undefined)}
                errors={errors?.lunch_break}
            />
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                <FormField 
                    name='postcode' 
                    label='Postcode'
                    value={form.postcode ?? ''}
                    onChange={(e) => updateForm('postcode', e.target.value || '')}
                    errors={errors?.postcode}
                    sx={{ flex: 1 }}
                />
                <Button variant='outlined' color='primary' onClick={prepopulateCoordinates}>
                    Get Coordinates
                </Button>
            </Box>
            <FormField 
                name='lat' 
                label='Latitude' 
                type='number' 
                value={form.lat ?? ''}
                onChange={(e) => updateForm('lat', e.target.value ? Number(e.target.value) : undefined)}
                errors={errors?.lat}
            />
            <FormField 
                name='lng' 
                label='Longitude' 
                type='number' 
                value={form.lng ?? ''}
                onChange={(e) => updateForm('lng', e.target.value ? Number(e.target.value) : undefined)}
                errors={errors?.lng}
            />
            <FormField 
                name='practice_name' 
                label='Practice Name'
                value={form.practice_name ?? ''}
                onChange={(e) => updateForm('practice_name', e.target.value)}
                errors={errors?.practice_name}
            />
            <FormField 
                select
                name='payment_option' 
                label='Payment Option'
                value={form.payment_option ?? ''}
                onChange={(e) => updateForm('payment_option', e.target.value)}
                errors={errors?.payment_option}>
                {paymentOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </FormField>
            <FormField 
                name='settlement_days' 
                label='Settlement Days' 
                type='number' 
                value={form.settlement_days ?? ''}
                onChange={(e) => updateForm('settlement_days', e.target.value ? Number(e.target.value) : undefined)}
                errors={errors?.settlement_days}
            />
            <FormField 
                select
                disabled={true}
                name='role' 
                label='Role'
                value={form.role ?? ''}
                onChange={(e) => updateForm('role', e.target.value)}
                errors={errors?.role}>
                <MenuItem value=''>Select Role</MenuItem>
                {roleOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </FormField>
            <MultiSelect
                name='tiers'
                label='Tiers'
                width='100%'
                options={[
                    'good',
                    'new',
                    'bad',
                    'unapproved',
                ]}
                value={form.tiers ?? []}
                onChange={(e) => updateForm('tiers', e.target.value)}
                errors={errors?.tiers}
            />
            {nurseCounts ? (
                <Alert severity='info'>
                    {nurseCounts.num_good_tier} Good Tier Nurses <br />
                    {nurseCounts.num_new_tier} New Tier Nurses <br />
                    {nurseCounts.num_bad_tier} Bad Tier Nurses <br />
                    {nurseCounts.num_unapproved_tier} Unapproved Tier Nurses <br />
                </Alert>
            ): null}
            
            {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                    disabled={true}
                    control={
                        <Checkbox
                            checked={form.sedation_required ?? false}
                            onChange={(e) => updateForm('sedation_required', e.target.checked)}
                        />
                    }
                    label="Sedation Required"
                />
                <FormControlLabel
                    disabled={true}
                    control={
                        <Checkbox
                            checked={form.radiography_required ?? false}
                            onChange={(e) => updateForm('radiography_required', e.target.checked)}
                        />
                    }
                    label="Radiography Required"
                />
                <FormControlLabel
                    disabled={true}
                    control={
                        <Checkbox
                            checked={form.implant_required ?? false}
                            onChange={(e) => updateForm('implant_required', e.target.checked)}
                        />
                    }
                    label="Implant Required"
                />
                <FormControlLabel
                    disabled={true}
                    control={
                        <Checkbox
                            checked={form.orthodontics_required ?? false}
                            onChange={(e) => updateForm('orthodontics_required', e.target.checked)}
                        />
                    }
                    label="Orthodontics Required"
                />
            </Box> */}
            <Alert severity='info'>
                Email and SMS will be sent to nurses when you submit this availability check. <br />
                Double check the details before submitting.
            </Alert>
            <Button variant='contained' color='primary' onClick={save} disabled={loading}>
                {loading ? 'Please wait...' : 'Create Availability Check'}
            </Button>
            <Button variant='outlined' color='primary' onClick={onCancel} disabled={loading}>
                Cancel
            </Button>
        </Box>
    )
}

export default AvailabilityCheckModal